import { useState, useEffect } from "react";
import styles from "./Status.module.css";
import Input from "../../Components/Forms/Input";
import Button from "../../Components/Forms/Button";
import SelectedList from "../../Components/SelectedList/SelectedList";
import Table from "../../Components/Table/Table";
import { MdUpload } from "react-icons/md";
import {
  formatUploadDate,
  getDefaultScheduleDate,
  optionsType,
} from "../../Components/Helper";
import { fetchPost } from "../../Services/Slices/postSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchGetFiles } from "../../Services/Slices/getFilesSlice";
import { showAppToast } from "../../Components/Toast/showAppToast";
import { showCertificateToast } from "../../Components/Toast/showCertificateToast";
import services from "../../Services/services";
import { ptBR } from "date-fns/locale/pt-BR";
import { LuCalendarDays } from "react-icons/lu";
import {
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@geti/identidade-visual-react";

function formatDisplayDate(date: Date) {
  return date.toLocaleDateString("pt-BR");
}

const Status = () => {
  const dispatch = useDispatch();
  const post = useSelector((state: any) => state.postSlice);
  const getFiles = useSelector((state: any) => state.getFilesSlice);
  const deleteFile = useSelector((state: any) => state.deleteFileSlice);
  const [page, setPage] = useState<number>(1);
  const [isDispatched, setIsDispatched] = useState<boolean>(false);
  const [isResponsive, setIsResponsive] = useState<boolean>(false);
  const [scheduleDateOpen, setScheduleDateOpen] = useState(false);
  const [form, setForm] = useState<{
    file: File | null;
    date: Date;
    type: string[];
    code: string;
    time: string;
  }>({
    file: null,
    date: getDefaultScheduleDate(),
    type: [],
    code: "",
    time: "",
  });
  const columns = [
    { title: "Nome", property: "name" },
    { title: "Data", property: "date" },
    { title: "Arquivo", property: "presigned_url" },
    { title: "Excluir", property: "delete" },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    if (name === "date") {
      setForm((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      const nextValue = value;
      setForm((prev: any) => {
        if (Array.isArray(prev[name])) {
          return {
            ...prev,
            [name]: [...prev[name], nextValue],
          };
        }
        return {
          ...prev,
          [name]: nextValue,
        };
      });
    }
  };

  const handleTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const numericValue = value.replace(/\D/g, "").toString();

    if (
      numericValue.length > 0 &&
      (parseInt(numericValue[0], 10) > 2 || parseInt(numericValue[0], 10) > 2)
    ) {
      return;
    }

    if (numericValue === "") {
      setForm((prev: any) => ({
        ...prev,
        time: "",
      }));
    }

    let formattedValue = numericValue;
    if (numericValue.length > 2 && !numericValue.includes(":")) {
      formattedValue = numericValue.slice(0, 2) + ":" + numericValue.slice(2);
    }

    if (formattedValue.length > 5) {
      formattedValue = formattedValue.substring(0, 5);
    }

    setForm((prev: any) => ({
      ...prev,
      time: formattedValue,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    const fileExtension = file?.name.split(".").pop();
    if (fileExtension === "docx") {
      setForm((prev: any) => ({
        ...prev,
        file: file,
      }));
    } else if (fileExtension !== "docx") {
      setForm((prev) => ({
        ...prev,
        file: null,
      }));
      showAppToast("fileInvalid");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.file || !form.date || form.type.length === 0 || !form.code) {
      showAppToast("postInvalid");
      return;
    }

    const formData = new FormData();
    formData.append("file", form.file);
    formData.append("post_type", form.type.join(","));
    formData.append("date", formatUploadDate(form.date));
    formData.append("hour", form.time);
    formData.append("number", form.code);
    dispatch<any>(fetchPost(formData));
    setForm({
      file: null,
      date: getDefaultScheduleDate(),
      type: [],
      code: "",
      time: "",
    });
  };

  const transformedData = getFiles?.data?.results?.map((item: any) => {
    const fileNameMatch = item.file_name.match(/name=(.*?)\./);
    const fileName = fileNameMatch ? fileNameMatch[1] : "Unknown File";

    const dateMatch = fileName.match(/date=(.*?)---/);
    const date = dateMatch ? dateMatch[1].replace(/-/g, "/") : "Unknown Date";

    const newFileNameMatch = item.file_name.match(/file=(.*?)\./);
    const newFileName = newFileNameMatch
      ? newFileNameMatch[1].replace("_", " ")
      : "Unknown File";

    return {
      name: newFileName,
      date: date,
      presigned_url: item.presigned_url,
      delete: item.file_name.replace("files/", ""),
    };
  });

  useEffect(() => {
    dispatch<any>(fetchGetFiles(page.toString()));
    setIsDispatched(true);
  }, [dispatch, page, deleteFile?.data?.response]);

  useEffect(() => {
    services
      .getCertificateStatus()
      .then((response: any) => showCertificateToast(response.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (getFiles.error) {
      showAppToast("scheduleError");
    }
  }, [getFiles.error]);

  useEffect(() => {
    if (post.error) {
      showAppToast("postError");
    }
  }, [post.error]);

  useEffect(() => {
    const handleResize = () => {
      setIsResponsive(window.innerWidth <= 750);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.postContainer}>
        <div className={form.file ? styles.fileContainer : undefined}>
          <label className={styles.fakeInput} htmlFor="file">
            <MdUpload size={isResponsive ? 18 : 24} />
          </label>
          <Input
            className={styles.file}
            name="file"
            type="file"
            id="file"
            onChange={handleFileChange}
          />
          {form.file && (
            <div className={styles.fileText}>Arquivo: {form.file.name}</div>
          )}
        </div>
        <div className={styles.calendarContainer}>
          <Popover open={scheduleDateOpen} onOpenChange={setScheduleDateOpen}>
            <PopoverTrigger
              type="button"
              className={styles.datePickerTrigger}
              aria-label="Data de publicação"
            >
              <LuCalendarDays className={styles.datePickerIcon} aria-hidden />
              <span className={styles.datePickerLabel}>
                {formatDisplayDate(form.date)}
              </span>
            </PopoverTrigger>
            <PopoverContent align="start" side="bottom" className="z-[100]">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(date) => {
                  if (date) {
                    setForm((prev) => ({ ...prev, date }));
                  }
                  setScheduleDateOpen(false);
                }}
                locale={ptBR}
                defaultMonth={form.date}
                captionLayout="dropdown"
                startMonth={new Date(new Date().getFullYear() - 10, 0)}
                endMonth={new Date(new Date().getFullYear() + 8, 11)}
                reverseYears
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className={styles.type}>
          <SelectedList
            placeholder="Tipo*"
            field="type"
            list={form}
            setList={setForm}
            options={optionsType}
            isType
            readOnly
          />
        </div>
        <div className={styles.lastColumn}>
          <Input
            className={styles.input}
            placeholder="Número*"
            name="code"
            value={form.code}
            onChange={handleChange}
            max={10}
          />
          <Input
            className={styles.time}
            placeholder="Horário"
            name="time"
            value={form.time}
            onChange={handleTime}
          />
        </div>
        <Button
          className={`${styles.button} ${styles.schedule}`}
          onClick={handleSubmit}
        >
          Agendar
        </Button>
      </div>
      <div className={styles.table}>
        <Table
          title="Edições Agendadas"
          columns={columns}
          data={transformedData}
          setPage={setPage}
          page={page}
          total={getFiles.data.count}
          downloadButton
          isEmpty={isDispatched && getFiles?.data?.results?.length === 0}
          isStatus
          loading={post.loading || getFiles.loading}
          error={post.error || getFiles.error}
        />
      </div>
    </div>
  );
};

export default Status;
